<template>
  <section class="how-it-works" ref="sectionRef">

    <!-- Floating botanical particles -->
    <div class="botanical-particles" aria-hidden="true">
      <span v-for="i in 8" :key="i" class="leaf" :style="leafStyle(i)"></span>
    </div>

    <div class="section-container">
      <!-- Section Header -->
      <div class="section-header" ref="headerRef">
        <div class="header-badge">
          <span class="badge-line"></span>
          <svg class="badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
          <span class="badge-line"></span>
        </div>
        <h2 class="section-title">{{ $t('howItWorks.title') }}</h2>
        <p class="section-subtitle">{{ $t('howItWorks.subtitle') }}</p>
        <div class="title-underline">
          <span class="underline-segment"></span>
          <span class="underline-diamond"></span>
          <span class="underline-segment"></span>
        </div>
      </div>

      <!-- Steps Flow -->
      <div class="steps-flow" ref="flowRef">
        <!-- Connecting Path -->
        <div class="connecting-path" aria-hidden="true">
          <svg viewBox="0 0 2 800" preserveAspectRatio="none">
            <defs>
              <linearGradient id="pathGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#059669" stop-opacity="0.6"/>
                <stop offset="50%" stop-color="#D4A373" stop-opacity="0.8"/>
                <stop offset="100%" stop-color="#059669" stop-opacity="0.6"/>
              </linearGradient>
            </defs>
            <line x1="1" y1="0" x2="1" y2="800" stroke="url(#pathGrad)" stroke-width="2" stroke-dasharray="4 6" class="path-line"/>
          </svg>
        </div>

        <!-- Step 1: ثبت محصول -->
        <div class="step-card card-1" ref="step1Ref" :class="{ visible: stepsVisible[0] }">
          <div class="step-number-wrap">
            <div class="step-number-glow"></div>
            <div class="step-number">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                <path d="M22 4L12 14.01l-3-3"/>
              </svg>
            </div>
            <span class="step-label">{{ $t('howItWorks.step1_label') }}</span>
          </div>
          <div class="step-content">
            <div class="step-icon-wrap">
              <div class="step-icon-bg"></div>
              <svg class="step-icon" viewBox="0 0 64 64" fill="none">
                <rect x="8" y="20" width="48" height="40" rx="4" stroke="#059669" stroke-width="2.5"/>
                <path d="M32 20V8M26 14h12" stroke="#059669" stroke-width="2.5" stroke-linecap="round"/>
                <circle cx="32" cy="40" r="8" stroke="#D4A373" stroke-width="2"/>
                <path d="M32 36v8M28 40h8" stroke="#D4A373" stroke-width="2.5" stroke-linecap="round"/>
                <rect x="12" y="48" width="8" height="8" rx="1" fill="#059669" fill-opacity="0.2"/>
                <rect x="24" y="46" width="8" height="10" rx="1" fill="#059669" fill-opacity="0.2"/>
                <rect x="36" y="44" width="8" height="12" rx="1" fill="#059669" fill-opacity="0.2"/>
                <rect x="48" y="42" width="4" height="14" rx="1" fill="#059669" fill-opacity="0.2"/>
              </svg>
            </div>
            <h3 class="step-title">{{ $t('howItWorks.step1_title') }}</h3>
            <div class="step-desc-wrap">
              <p class="step-desc">{{ $t('howItWorks.step1_desc') }}</p>
            </div>
            <div class="step-tags">
              <span class="tag" v-for="tag in step1Tags" :key="tag">{{ tag }}</span>
            </div>
          </div>
        </div>

        <!-- Step 2: بازاریابی و فروش -->
        <div class="step-card card-2" ref="step2Ref" :class="{ visible: stepsVisible[1] }">
          <div class="step-number-wrap">
            <div class="step-number-glow"></div>
            <div class="step-number">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M12 20h9"/>
                <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
            </div>
            <span class="step-label">{{ $t('howItWorks.step2_label') }}</span>
          </div>
          <div class="step-content">
            <div class="step-icon-wrap">
              <div class="step-icon-bg"></div>
              <svg class="step-icon" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="24" stroke="#D4A373" stroke-width="2.5"/>
                <path d="M24 32l6 6 10-10" stroke="#059669" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 56C16 48 20 40 24 32" stroke="#D4A373" stroke-width="2" stroke-dasharray="3 3"/>
                <path d="M56 56C48 48 44 40 40 32" stroke="#D4A373" stroke-width="2" stroke-dasharray="3 3"/>
                <circle cx="32" cy="32" r="4" fill="#059669"/>
                <!-- Signal waves -->
                <path d="M22 22C26 18 30 16 32 16" stroke="#D4A373" stroke-width="1.5" stroke-dasharray="2 2" opacity="0.5"/>
                <path d="M42 22C38 18 34 16 32 16" stroke="#D4A373" stroke-width="1.5" stroke-dasharray="2 2" opacity="0.5"/>
              </svg>
            </div>
            <h3 class="step-title">{{ $t('howItWorks.step2_title') }}</h3>
            <div class="step-desc-wrap">
              <p class="step-desc">{{ $t('howItWorks.step2_desc') }}</p>
            </div>
            <div class="step-tags">
              <span class="tag" v-for="tag in step2Tags" :key="tag">{{ tag }}</span>
            </div>
          </div>
        </div>

        <!-- Step 3: تحویل -->
        <div class="step-card card-3" ref="step3Ref" :class="{ visible: stepsVisible[2] }">
          <div class="step-number-wrap">
            <div class="step-number-glow"></div>
            <div class="step-number">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M5 12h14"/>
                <path d="M12 5l7 7-7 7"/>
              </svg>
            </div>
            <span class="step-label">{{ $t('howItWorks.step3_label') }}</span>
          </div>
          <div class="step-content">
            <div class="step-icon-wrap">
              <div class="step-icon-bg"></div>
              <svg class="step-icon" viewBox="0 0 64 64" fill="none">
                <rect x="4" y="24" width="56" height="32" rx="6" stroke="#059669" stroke-width="2.5"/>
                <path d="M20 48h24" stroke="#059669" stroke-width="2" stroke-linecap="round"/>
                <path d="M44 24V16a4 4 0 00-4-4H24a4 4 0 00-4 4v8" stroke="#059669" stroke-width="2.5"/>
                <circle cx="22" cy="40" r="4" stroke="#D4A373" stroke-width="2"/>
                <circle cx="42" cy="40" r="4" stroke="#D4A373" stroke-width="2"/>
                <path d="M28 36l3 3 5-5" stroke="#059669" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 32h6M48 32h6" stroke="#D4A373" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
            <h3 class="step-title">{{ $t('howItWorks.step3_title') }}</h3>
            <div class="step-desc-wrap">
              <p class="step-desc">{{ $t('howItWorks.step3_desc') }}</p>
            </div>
            <div class="step-tags">
              <span class="tag" v-for="tag in step3Tags" :key="tag">{{ tag }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Video Section -->
      <div class="video-section" ref="videoRef" :class="{ visible: videoVisible }">
        <div class="video-border"></div>
        <div class="video-wrapper">
          <div class="video-header">
            <svg class="play-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="12" cy="12" r="10"/>
              <path d="M10 8v8l6-4-6-4z"/>
            </svg>
            <span>{{ $t('howItWorks.video_title') }}</span>
          </div>
          <div class="h_iframe-aparat_embed_frame">
            <span style="display: block;padding-top: 57%"></span>
            <iframe src="https://www.aparat.com/video/video/embed/videohash/hayh5r4/vt/frame?titleShow=true" loading="lazy" allowFullScreen="true" webkitallowfullscreen="true" mozallowfullscreen="true"></iframe>
          </div>
        </div>
      </div>

      <!-- CTA -->
      <div class="cta-section" ref="ctaRef" :class="{ visible: ctaVisible }">
        <div class="cta-glow"></div>
        <NuxtLink :to="localePath('/about-sarvban')" class="cta-button">
          <svg class="cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M5 12h14"/>
            <path d="M12 5l7 7-7 7"/>
          </svg>
          <span class="cta-text">{{ $t('howItWorks.cta') }}</span>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<script setup>
const { tm, rt } = useI18n()
const localePath = useLocalePath()

const sectionRef = ref(null)
const headerRef = ref(null)
const flowRef = ref(null)
const step1Ref = ref(null)
const step2Ref = ref(null)
const step3Ref = ref(null)
const videoRef = ref(null)
const ctaRef = ref(null)

const stepsVisible = reactive([false, false, false])

function resolveTags(key) {
  const raw = tm(key)
  return Array.isArray(raw) ? raw.map(m => rt(m)) : []
}

const step1Tags = computed(() => resolveTags('howItWorks.step1_tags'))
const step2Tags = computed(() => resolveTags('howItWorks.step2_tags'))
const step3Tags = computed(() => resolveTags('howItWorks.step3_tags'))
const videoVisible = ref(false)
const ctaVisible = ref(false)

function leafStyle(i) {
  const size = 12 + Math.random() * 20
  const left = 5 + Math.random() * 90
  const delay = Math.random() * 8
  const duration = 8 + Math.random() * 12
  const startY = Math.random() * 100
  return {
    width: `${size}px`,
    height: `${size * 1.4}px`,
    left: `${left}%`,
    top: `${startY}%`,
    animationDelay: `${delay}s`,
    animationDuration: `${duration}s`,
    opacity: 0.08 + Math.random() * 0.12,
    transform: `rotate(${Math.random() * 360}deg)`
  }
}

function onScroll() {
  if (!sectionRef.value) return

  const sectionTop = sectionRef.value.getBoundingClientRect().top
  const windowHeight = window.innerHeight
  const revealPoint = 100

  if (sectionTop < windowHeight - revealPoint) {
    // Animate step cards with staggered delays
    const steps = [step1Ref, step2Ref, step3Ref]
    steps.forEach((ref, i) => {
      if (!ref.value) return
      const top = ref.value.getBoundingClientRect().top
      if (top < windowHeight - revealPoint) {
        stepsVisible[i] = true
      }
    })

    if (videoRef.value) {
      const videoTop = videoRef.value.getBoundingClientRect().top
      if (videoTop < windowHeight - revealPoint) {
        videoVisible.value = true
      }
    }

    if (ctaRef.value) {
      const ctaTop = ctaRef.value.getBoundingClientRect().top
      if (ctaTop < windowHeight - revealPoint) {
        ctaVisible.value = true
      }
    }
  }
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})
</script>

<style  lang="scss" scoped>
@use "sass:color";

.how-it-works {
  position: relative;
  /* background: linear-gradient(170deg, #0a1a0a 0%, #112211 30%, #1a2e1a 60%, #0f1f0f 100%); */
  padding: 80px 0 60px;
  overflow: hidden;
  direction: inherit;
  background: linear-gradient(136deg, #f8f6f3, #f8f1f0);
}

/* Background Layers */
.bg-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.bg-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
}

.glow-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(5, 150, 105, 0.15), transparent);
  top: -200px;
  right: -100px;
}

.glow-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(212, 163, 115, 0.1), transparent);
  bottom: 0;
  left: -100px;
}

.glow-3 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(5, 150, 105, 0.08), transparent);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.noise-overlay {
  position: absolute;
  inset: 0;
  opacity: 0.03;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
  background-size: 256px;
}

/* Floating Botanical Particles */
.botanical-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.leaf {
  position: absolute;
  background: #059669;
  border-radius: 0 50% 50% 50%;
  animation: leaf-float linear infinite;
  filter: blur(1px);
}

@keyframes leaf-float {
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 0.12;
  }
  90% {
    opacity: 0.12;
  }
  100% {
    transform: translateY(-100vh) rotate(720deg);
    opacity: 0;
  }
}

/* Container */
.section-container {
  /*max-width: 800px;*/
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 2;
}

/* Section Header */
.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.header-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 20px;
}

.badge-line {
  width: 40px;
  height: 1px;
  background: linear-gradient(90deg, transparent, #059669);
}

.badge-line:last-child {
  background: linear-gradient(90deg, #059669, transparent);
}

.badge-icon {
  width: 20px;
  height: 20px;
  color: #D4A373;
}

.section-title {
  font-size: 2rem;
  font-weight: 800;
  color: $secondary;
  margin: 0 0 12px;
  letter-spacing: 1px;
}

.section-subtitle {
  font-size: 1rem;
  color: #a8b5a0;
  margin: 0 0 24px;
  font-weight: 400;
}

.title-underline {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.underline-segment {
  width: 60px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #059669, transparent);
  border-radius: 1px;
}

.underline-diamond {
  width: 8px;
  height: 8px;
  background: #D4A373;
  transform: rotate(45deg);
  border-radius: 1px;
}

/* Steps Flow */
.steps-flow {
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 10px;
  padding-bottom: 20px;
}

/* Connecting Path */
.connecting-path {
  position: absolute;
  top: 0;
  bottom: 60px;
  left: 60px;
  width: 2px;
  z-index: 1;
}

.path-line {
  animation: path-pulse 3s ease-in-out infinite;
}

@keyframes path-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

/* Step Card */
.step-card {
  position: relative;
  z-index: 2;
  display: flex;
  gap: 20px;
  opacity: 0;
  transform: translateY(40px);
  transition: all 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

.step-card.visible {
  opacity: 1;
  transform: translateY(0);
}

.card-1 { transition-delay: 0s; }
.card-2 { transition-delay: 0.2s; }
.card-3 { transition-delay: 0.4s; }

/* Step Number */
.step-number-wrap {
  display: none;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  position: relative;
  width: 60px;
}

.step-number-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 70px;
  height: 70px;
  background: radial-gradient(circle, rgba(5, 150, 105, 0.2), transparent);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: glow-pulse 2s ease-in-out infinite;
}

@keyframes glow-pulse {
  0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
  50% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
}

.step-number {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(5, 150, 105, 0.2), rgba(5, 150, 105, 0.1));
  border: 1.5px solid rgba(5, 150, 105, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  backdrop-filter: blur(8px);
  transition: all 0.4s ease;
}

.step-number svg {
  width: 22px;
  height: 22px;
  color: #D4A373;
}

.step-card:hover .step-number {
  background: linear-gradient(135deg, rgba(5, 150, 105, 0.4), rgba(5, 150, 105, 0.2));
  border-color: #059669;
  transform: scale(1.1);
  box-shadow: 0 0 30px rgba(5, 150, 105, 0.3);
}

.step-label {
  font-size: 0.65rem;
  color: #6b8a6b;
  font-weight: 500;
  letter-spacing: 1px;
}

/* Step Content */
.step-content {
  flex: 1;
  background: linear-gradient(145deg, rgba(30, 50, 30,1), rgba(20, 35, 20, 1));
  border: 1px solid rgba(5, 150, 105, 0.15);
  border-radius: 20px;
  padding: 28px;
  backdrop-filter: blur(12px);
  transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}

.step-card:hover .step-content {
  border-color: rgba(5, 150, 105, 0.4);
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.3), 0 0 30px rgba(5, 150, 105, 0.05);
  transform: translateX(-4px);
}

.step-icon-wrap {
  width: 56px;
  height: 56px;
  position: relative;
  margin-bottom: 16px;
}

.step-icon-bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, rgba(5, 150, 105, 0.2), transparent);
  border-radius: 50%;
  animation: icon-breathe 3s ease-in-out infinite;
}

@keyframes icon-breathe {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.15); opacity: 0.8; }
}

.step-icon {
  width: 56px;
  height: 56px;
  position: relative;
  z-index: 2;
  filter: drop-shadow(0 2px 8px rgba(5, 150, 105, 0.2));
}

.step-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #f5f0e8;
  margin: 0 0 12px;
}

.step-desc-wrap {
  margin-bottom: 16px;
}

.step-desc {
  font-size: 0.9rem;
  color: #a8b5a0;
  line-height: 1.8;
  margin: 0;
}

.step-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  display: inline-block;
  padding: 4px 14px;
  background: rgba(5, 150, 105, 0.1);
  border: 1px solid rgba(5, 150, 105, 0.2);
  border-radius: 20px;
  font-size: 0.7rem;
  color: #6ee7b7;
  font-weight: 500;
}

/* Video Section */
.video-section {
  margin: 0 auto;
  max-width: 800px;
  position: relative;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.2s;
}

.video-section.visible {
  opacity: 1;
  transform: translateY(0);
}

.video-border {
  position: absolute;
  inset: -3px;
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(5, 150, 105, 0.3), rgba(212, 163, 115, 0.3), rgba(5, 150, 105, 0.3));
  z-index: 0;
  animation: border-shimmer 4s ease-in-out infinite;
}

@keyframes border-shimmer {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

.video-wrapper {
  position: relative;
  z-index: 1;
  background: rgba(15, 25, 15, 0.9);
  border-radius: 21px;
  padding: 20px;
  overflow: hidden;
}

.video-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.play-icon {
  width: 24px;
  height: 24px;
  color: #059669;
}

.video-header span {
  font-size: 0.9rem;
  color: #f5f0e8;
  font-weight: 600;
}

.h_iframe-aparat_embed_frame {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
}

.h_iframe-aparat_embed_frame .ratio {
  display: block;
  width: 100%;
  height: auto;
}

.h_iframe-aparat_embed_frame iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

/* CTA Section */
.cta-section {
  margin-top: 40px;
  text-align: center;
  position: relative;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.4s;
}

.cta-section.visible {
  opacity: 1;
  transform: translateY(0);
}

.cta-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(5, 150, 105, 0.15), transparent);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(135deg, #059669, #047857);
  color: #fff;
  border-radius: 16px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  position: relative;
  transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
  box-shadow: 0 4px 20px rgba(5, 150, 105, 0.3);
}

.cta-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 40px rgba(5, 150, 105, 0.4);
  background: linear-gradient(135deg, #059669, #03684a);
}

.cta-arrow {
  width: 18px;
  height: 18px;
  transition: transform 0.3s ease;
}

.cta-button:hover .cta-arrow {
  transform: translateX(-4px);
}

/* Responsive */
@media (max-width: 640px) {
  .steps-flow{
    flex-direction: column;
  }
  .how-it-works {
    padding: 60px 0 40px;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .step-card {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
  }

  .connecting-path {
    left: 50%;
    transform: translateX(-50%);
  }

  .step-number-wrap {
    width: auto;
    flex-direction: row;
    gap: 12px;
  }

  .step-content {
    padding: 24px 20px;
  }

  .step-icon-wrap {
    margin: 0 auto 16px;
  }

  .step-tags {
    justify-content: center;
  }

  .cta-button {
    padding: 14px 24px;
    font-size: 0.8rem;
    width: 100%;
    justify-content: center;
  }
}

@media (min-width: 768px) {
  .how-it-works {
    padding: 100px 0 80px;
  }

  .section-title {
    font-size: 2.4rem;
  }

  .step-content {
    padding: 32px;
  }
}
</style>
